import { AxiosInstance, AxiosPromise, AxiosRequestConfig } from 'axios';

import { MatrixReportResponseRaw } from '~src/interfaces/heatmap.interface';
import { SessionUserI } from '~src/interfaces/session.types';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { AnswerResponse } from '~src/modules/reports/interfaces/answer.interface';
import { AnswerSummary, ReportEntityTypes, ReportsObj } from '~src/modules/reports/interfaces/header.interface';
import { QuestionResponse, QuestionSummary } from '~src/modules/reports/interfaces/questions.interface';
import { TopicsResponse } from '~src/modules/reports/interfaces/topics.interface';
import { buildSummaryUrl } from '~src/modules/reports/reportsHeader/actions/headerActions';
import { TopicListFilters } from '~src/modules/reports/shared/components/topics/list/SubjectTopicsList';
import { PeriodValues, SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import {
  FetchSurveysCyclesParams,
  FetchSurveysCyclesResponse,
} from '~src/modules/surveys/actions/cyclesActions';
import { GetSpiderGraphPayload, GetSpiderGraphResponse } from '~src/modules/surveys/interfaces';
import { GetAdminsResponse } from '~src/modules/users/interfaces';
import { getUrlFromObject } from '~src/utils';

function arrayUnique<T extends string>(array: T[]): T[] {
  return Array.from(new Set(array));
}

export interface AnalysisLineRequest {
  cycleIds?: string[];
  aboutSegmentIds: string[];
  askSegmentIds?: string[];
  surveyId: string;
  topicId?: string;
  surveySubject: SurveySubjectTypes;
  isSelfAssessmentIncluded: boolean;
  isTeamSegmentIncluded: boolean;
  feedbackCycle?: string;
}

export interface RecommendationsRequest {
  cycleIds: string[];
  segmentIds: string[];
  surveyId: string;
  topicId: string;
  surveySubject: SurveySubjectTypes;
}

export interface FeedbackPeriodRequests {
  feedbackCycle: string;
  surveySubject: SurveySubjectTypes;
  surveyId: string;
  aboutSegmentId: string;
  includeSelfAssessment: boolean;
}

interface FetchLinesParams {
  filterAttributeOptions: string[];
  askSegmentIds?: string[];
  filterAnsweredRange?: PeriodValues;
  cycles?: string[];
  id?: number;
  page?: number | string;
  per_page?: number;
  filterSurvey?: string;
  filterSubject?: SurveySubjectTypes;
  filterTopicId?: string;
  includeSelfAssessment?: boolean;
  includeTeamSegment?: boolean;
  feedbackCycle?: string;
}

enum AxesType {
  Topics = 'topics',
  Questions = 'questions',
}

export interface GetAdminsParams {
  page?: number;
  sort?: SortingValues;
  per_page?: number;
}

export class Reporting {
  constructor(private axios: AxiosInstance) {}

  getSurveysCycles(params: FetchSurveysCyclesParams): AxiosPromise<FetchSurveysCyclesResponse> {
    const searchParams = new URLSearchParams();
    params.surveyIds.forEach(surveyId => searchParams.append('surveys[]', surveyId));
    return this.axios.get(`reports/cycles?${searchParams.toString()}`);
  }

  async getAdmins(config?: AxiosRequestConfig): Promise<GetAdminsResponse> {
    const response = await this.axios.get<GetAdminsResponse>(`/admins`, config);
    return response.data;
  }

  fetchLines(requestedLines: AnalysisLineRequest[]): AxiosPromise<MatrixReportResponseRaw> {
    if (!requestedLines.length) {
      return Promise.reject('no lines requested');
    }

    if (requestedLines.length > 1) {
      return Promise.reject('logic error! more than one requested line is not implemented yet.');
    }

    const requestedLine = requestedLines[0];
    const axesType = requestedLine.topicId ? AxesType.Questions : AxesType.Topics;

    if (!requestedLine.aboutSegmentIds.length) {
      return Promise.reject('no segments selected.');
    }

    let params: FetchLinesParams = {
      filterSubject: requestedLine.surveySubject,
      filterSurvey: requestedLine.surveyId,
      filterTopicId: requestedLine.topicId,
      filterAttributeOptions: arrayUnique(requestedLine.aboutSegmentIds),
      includeSelfAssessment: requestedLine.isSelfAssessmentIncluded,
    };

    if (requestedLine.feedbackCycle) {
      params = {
        ...params,
        feedbackCycle: requestedLine.feedbackCycle,
      };
    } else {
      params = {
        ...params,
        filterAnsweredRange: PeriodValues.AllTime,
        askSegmentIds: requestedLine.askSegmentIds && arrayUnique(requestedLine.askSegmentIds),
        cycles: requestedLine.cycleIds && arrayUnique(requestedLine.cycleIds),
      };
    }

    return this.axios.get(
      `reports/matrix/${axesType}${getUrlFromObject({ ...params }, [
        'filterAttributeOptions',
        'cycles',
        'askSegmentIds',
      ])}`,
    );
  }

  async getSpiderGraph(params: GetSpiderGraphPayload): Promise<GetSpiderGraphResponse> {
    const response = await this.axios.get(`reports/matrix/${AxesType.Topics}${getUrlFromObject({ ...params })}`);
    return response.data;
  }

  fetchRecommendations(args: RecommendationsRequest[]): AxiosPromise<MatrixReportResponseRaw> {
    if (!args.length) {
      return Promise.reject('no data requested');
    }

    if (args.length > 1) {
      return Promise.reject('logic error! more than one requested line is not implemented yet.');
    }

    const arg = args[0];

    if (!arg.segmentIds.length) {
      return Promise.reject('no segments selected.');
    }

    const params: FetchLinesParams = {
      filterAnsweredRange: PeriodValues.AllTime,
      filterSubject: arg.surveySubject,
      filterSurvey: arg.surveyId,
      filterTopicId: arg.topicId,
      filterAttributeOptions: arrayUnique(arg.segmentIds),
      askSegmentIds: [], // TODO rly?
      cycles: arrayUnique(arg.cycleIds),
    };

    return this.axios.get(
      `recommendations${getUrlFromObject({ ...params }, [
        'filterAttributeOptions',
        'cycles',
        'askSegmentIds',
      ])}`,
    );
  }

  fetchUserTopicsList(
    params: Omit<TopicListFilters, 'filterSubject'>,
    userId: string,
  ): AxiosPromise<TopicsResponse> {
    const queryStr = getUrlFromObject({
      ...params,
      filterSubject: SurveySubjectTypes.People,
    });
    return this.axios.get(`/reports/user/${userId}/topics${queryStr}`);
  }

  fetchUserQuestionsList(
    topicId: string,
    userId: string,
    params: { [key: string]: string | null },
  ): AxiosPromise<QuestionResponse> {
    const queryStr = getUrlFromObject({
      ...params,
    });
    return this.axios.get(`/reports/user/${userId}/topic/${topicId}/questions${queryStr}`);
  }

  fetchUserAnswersList(
    questionId: string,
    userId: string,
    params: { [key: string]: string | null },
  ): AxiosPromise<AnswerResponse> {
    const queryStr = getUrlFromObject({
      ...params,
    });
    return this.axios.get(`/reports/user/${userId}/question/${questionId}/answers${queryStr}`);
  }

  fetchTopicsSummary(
    topicId: string,
    feedbackCycleId: string,
    subjectType: ReportEntityTypes,
    surveyId: string | null | undefined,
    subject: ReportsObj,
    me?: SessionUserI,
  ): AxiosPromise<AnswerSummary> {
    let url = buildSummaryUrl(subjectType, subject, me ?? null);
    url += `/topic/${topicId}/questions/summary`;
    const query = [];

    if (feedbackCycleId) {
      query.push(`feedbackCycle=${feedbackCycleId}`);
    }

    if (surveyId) {
      query.push(`filterSurvey=${surveyId}`);
    }

    url += query.length > 0 ? '?' + query.join('&') : '';

    return this.axios.get(url);
  }

  fetchQuestionsSummary(
    questionId: string,
    feedbackCycleId: string,
    subjectType: ReportEntityTypes,
    surveyId: string | null | undefined,
    subject: ReportsObj,
    me?: SessionUserI | null,
  ): AxiosPromise<QuestionSummary> {
    let url = buildSummaryUrl(subjectType, subject, me ?? null);
    url += `/question/${questionId}/answers/summary`;
    const query = [];

    if (feedbackCycleId) {
      query.push(`feedbackCycle=${feedbackCycleId}`);
    }

    if (surveyId) {
      query.push(`filterSurvey=${surveyId}`);
    }

    url += query.length > 0 ? '?' + query.join('&') : '';

    return this.axios.get(url);
  }
}
